import { Suspense, lazy, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { ConsoleLayout } from './components/layout/ConsoleLayout';
import { useConsoleAuth } from './contexts/AuthContext';

// Auth pages (direct import - small)
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));

// Lazy-loaded pages
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const TenantsPage = lazy(() => import('./pages/tenants/TenantsPage').then(m => ({ default: m.TenantsPage })));
const TenantDetailPage = lazy(() => import('./pages/tenants/TenantDetailPage').then(m => ({ default: m.TenantDetailPage })));
const CreateTenantPage = lazy(() => import('./pages/tenants/CreateTenantPage').then(m => ({ default: m.CreateTenantPage })));
const BillingPage = lazy(() => import('./pages/billing/BillingPage').then(m => ({ default: m.BillingPage })));
const PlatformPage = lazy(() => import('./pages/platform/PlatformPage').then(m => ({ default: m.PlatformPage })));
const UsersPage = lazy(() => import('./pages/users/UsersPage').then(m => ({ default: m.UsersPage })));
const AdminsPage = lazy(() => import('./pages/admins/AdminsPage').then(m => ({ default: m.AdminsPage })));
const SystemPage = lazy(() => import('./pages/system/SystemPage').then(m => ({ default: m.SystemPage })));
const SupportPage = lazy(() => import('./pages/support/SupportPage').then(m => ({ default: m.SupportPage })));
const SecurityPage = lazy(() => import('./pages/security/SecurityPage').then(m => ({ default: m.SecurityPage })));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage').then(m => ({ default: m.ProfilePage })));
const RolesPage = lazy(() => import('./pages/roles/RolesPage').then(m => ({ default: m.RolesPage })));
const ConfigPage = lazy(() => import('./pages/config/ConfigPage').then(m => ({ default: m.ConfigPage })));
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useConsoleAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}

function GuestRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useConsoleAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
          <p className="text-gray-600">An unexpected error occurred. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Refresh Page
          </button>
        </div>
      }
    >
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" message="Loading page..." /></div>}>
      <Routes>
        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />

        <Route element={<ProtectedRoute><ConsoleLayout /></ProtectedRoute>}>
          <Route index element={<DashboardPage />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/new" element={<CreateTenantPage />} />
          <Route path="tenants/:id" element={<TenantDetailPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="platform" element={<PlatformPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="admins" element={<AdminsPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="system" element={<SystemPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="config" element={<ConfigPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </Sentry.ErrorBoundary>
  );
}
